import React from "react";
import { motion } from "framer-motion";
import { useJourney } from "@/context/JourneyContext";
import { StageFrame, Eyebrow, EASE } from "@/components/StageFrame";
import { ConsultantNav } from "@/components/Navigation";
import { Check, Circle, Gift } from "lucide-react";

const STEPS = [
  { n: 1, title: "Discovery", desc: "Lifestyle, household and initial affordability understood." },
  { n: 2, title: "Finance", desc: "Confirm borrowing capacity and comfortable repayment." },
  { n: 3, title: "Land Selection", desc: "Identify suitable land by location, price and fit." },
  { n: 4, title: "Floorplan & Inclusions", desc: "Personalise floorplan, facade, colours and upgrades.", upgrades: true },
  { n: 5, title: "Secure Your Home", desc: "Initial deposits for land and build." },
  { n: 6, title: "Final Price Confirmation", desc: "Confirm the full package price and weekly repayment." },
  { n: 7, title: "Contracts", desc: "Progress land and building contracts." },
];

export default function Roadmap() {
  const { data, next, back } = useJourney();
  const completed = 2; // Discovery + Finance
  const current = 3;   // Land Selection
  const rewards = data.rewards || [];
  const rewardTotal = rewards.reduce((s, r) => s + (r.value || 0), 0);

  return (
    <StageFrame testId="stage-roadmap" className="px-6 md:px-14 pt-20 pb-32">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-end justify-between flex-wrap gap-3">
          <div>
            <Eyebrow>Section 3 · Your Home Roadmap</Eyebrow>
            <h1 className="font-serif text-4xl md:text-6xl font-light tracking-tight text-forest mt-3">Your pathway to your new home.</h1>
          </div>
          <span data-testid="roadmap-progress" className="rounded-full border border-line px-4 py-2 text-sm text-forest num-annot">
            {completed} of 7 Steps Complete
          </span>
        </div>

        <div className="mt-10 relative">
          <div className="absolute left-[19px] top-2 bottom-2 w-px bg-line" />
          <div className="space-y-4">
            {STEPS.map((s, i) => {
              const done = s.n <= completed;
              const isCurrent = s.n === current;
              return (
                <motion.div key={s.n} data-testid={`roadmap-step-${s.n}`}
                  initial={{ opacity: 0, x: -14 }} animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, ease: EASE, delay: i * 0.08 }}
                  className={`relative flex gap-4 rounded-2xl border p-5 ${isCurrent ? "border-brand bg-brand/10" : "border-line bg-white/5"}`}>
                  <div className={`relative z-10 grid h-10 w-10 shrink-0 place-items-center rounded-full ${done ? "bg-brand text-white" : isCurrent ? "bg-forest text-bg" : "bg-surface text-mutedText"}`}>
                    {done ? <Check size={18} /> : <span className="text-sm font-medium">{s.n}</span>}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-3">
                      <h3 className="font-serif text-2xl text-forest">{s.title}</h3>
                      {done && <span className="text-xs text-brand font-medium">{s.n === 1 ? "Complete ✓" : "Appointment Booked ✓"}</span>}
                      {isCurrent && <span className="text-xs text-brand font-medium">You are here</span>}
                    </div>
                    <p className="text-mutedText text-sm mt-1">{s.desc}</p>
                    {s.upgrades && rewards.length > 0 && (
                      <div data-testid="roadmap-upgrades" className="mt-3 rounded-xl border border-brand/40 bg-brand/10 p-4">
                        <div className="flex items-center gap-2">
                          <Gift size={15} className="text-brand" />
                          <span className="text-sm text-forest font-medium">${rewardTotal.toLocaleString()} FREE Upgrades — auto-applied at checkout</span>
                        </div>
                        <div className="mt-2.5 space-y-1.5">
                          {rewards.map((r, i) => (
                            <div key={i} data-testid={`roadmap-upgrade-${i}`} className="flex items-center justify-between text-sm">
                              <span className="flex items-center gap-2 text-forest/90"><Check size={14} className="text-brand" /> {r.name}</span>
                              <span className="text-mutedText">${(r.value || 0).toLocaleString()} · Applied ✓</span>
                            </div>
                          ))}
                        </div>
                        {data.voucher?.code && (
                          <div data-testid="roadmap-voucher" className="mt-3 flex items-center justify-between rounded-lg border border-dashed border-brand/50 bg-brand/5 px-3 py-2">
                            <span className="text-xs text-mutedText">Voucher code — present to redeem</span>
                            <span className="font-mono text-sm font-semibold tracking-widest text-brand">{data.voucher.code}</span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>

      <ConsultantNav testId="stage-roadmap" onBack={back} onNext={next} nextLabel="Continue" />
    </StageFrame>
  );
}
